import type { QueryClient } from "@tanstack/react-query";
import type { VodItem } from "@hybrid-iptv/types";
import { api, runsOnDevice } from "../api/client.js";
import { activeProviderSourceId, providerBody } from "../epg/api.js";
import type { CatalogPage } from "./catalogQuery.js";
import { alternatesOf, dupKeyOf } from "./duplicates.js";

/**
 * The other streams of the same film, best first, as the player's fallbacks (owner, 2026-10-13).
 *
 * Browsing lists show one entry per film (duplicates.ts), so when the chosen stream will not play
 * the player moves on to another copy of the SAME film instead of failing: `altUrls` is what it
 * already does for a channel's mirrors. Nothing else changes about playing. An answer that cannot
 * be had (no catalog yet, an error) is no fallbacks, never a failed play.
 */

/** How many fallbacks go with a play: more only delays giving up on a dead title. */
const MAX_FALLBACKS = 3;

export async function alternateStreamUrls(queryClient: QueryClient | null, item: VodItem): Promise<string[]> {
  try {
    const key = dupKeyOf(item);
    if (key === null) return [];
    const sourceId = activeProviderSourceId();
    if (!sourceId) return [];
    if (runsOnDevice()) {
      const page = await api.post<CatalogPage<VodItem>>(`/api/sources/${encodeURIComponent(sourceId)}/vod/query`, {
        ...providerBody(),
        query: { kind: "vod", dupKey: key, limit: 8 },
        excludeCategories: [],
      });
      return page.items
        .filter((other) => other.id !== item.id && other.streamUrl)
        .map((other) => other.streamUrl)
        .slice(0, MAX_FALLBACKS);
    }
    // The website already holds the whole catalog in the page (useVodCatalog).
    const catalog = queryClient?.getQueryData<{ vod: VodItem[] }>(["vod", sourceId]);
    if (!catalog) return [];
    return alternatesOf(catalog.vod, item)
      .map((other) => other.streamUrl)
      .filter((url): url is string => Boolean(url))
      .slice(0, MAX_FALLBACKS);
  } catch {
    return [];
  }
}
